import { ActivityEntity } from '@surecompanies/activity-log'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { getApiUrl as getEnrollmentApiUrl } from 'modules/enrollment-platform-api'
import { getApiUrl as getPDFApiUrl } from 'modules/pdf-rest-api'
import getApiToken from 'enrollment-platform/api/get-api-token'
import {
  ActivityQueriesFilter,
  PaginatedActivity,
  ApiActivity,
} from 'types/activity'

/**
 * Fetches activity logs from the API with pagination
 *
 * @param options - The fetch options
 * @param options.filter - Filter criteria for activity logs
 * @param options.pageParam - The page number to fetch (1-based)
 *
 * @returns Promise<ActivityLogResponse> - The response containing activity log data and pagination info
 */
export const fetchActivityLogs = async (params?: {
  filter?: ActivityQueriesFilter
}): Promise<PaginatedActivity> => {
  const { filter } = params || {}
  const pageSize = filter?.limit || 20
  const page = filter?.page || 1

  const response = await axios.get('activity-log', {
    params: {
      ...filter,
      page,
      limit: pageSize,
    },
    baseURL: getEnrollmentApiUrl(),
  })

  // Handle different response structures
  const responseData = response.data.data
  const hasMore =
    response.data.pagination?.hasMore !== undefined
      ? response.data.pagination.hasMore
      : responseData.length === pageSize // If not explicitly provided, check if we got a full page

  const pagination = {
    currentPage: page,
    totalPages:
      response.data.pagination?.totalPages || (hasMore ? page + 1 : page),
    totalItems: response.data.pagination?.totalItems || responseData.length,
    hasMore,
  }

  return {
    data: responseData,
    pagination,
  }
}

export const createActivityLog = async (
  activity: ActivityEntity
): Promise<ApiActivity> => {
  try {
    const response: AxiosResponse = await axios.post('activities', activity, {
      baseURL: getPDFApiUrl(),
      headers: { Authorization: getApiToken() },
    })
    return response.data
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError
      throw new Error(
        axiosError.response?.data?.message ||
          axiosError.message ||
          'Failed to create activity log'
      )
    }
    throw new Error('An unexpected error occurred while creating activity log')
  }
}
